import { useEffect, useMemo, useRef, useState } from "react";
import { createDeltaChart, type DeltaChart } from "../chart/deltaChart.js";
import { CurrentValues } from "./CurrentValues.js";
import type { LinePoint, PaneState } from "../types.js";

interface Props {
  symbol: string;
  pane: PaneState;
  line: LinePoint[];
  show0dte: boolean;
}

/**
 * Left pane — cumulative signed delta-notional lines. The chart is redrawn from
 * the FULL line series each render (setData): correct, reconnect-safe, and
 * matches the SSE contract (each `pane` event carries the full line). The value
 * strip sits above the plot; hovering the chart rewinds it to that moment.
 */
export function LeftPane({ symbol, pane, line, show0dte }: Props): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  const chartRef = useRef<DeltaChart | null>(null);
  const [hoverSec, setHoverSec] = useState<number | null>(null);

  useEffect(() => {
    if (!ref.current || chartRef.current) return;
    const chart = createDeltaChart(ref.current);
    chart.onHover(setHoverSec);
    chartRef.current = chart;
    return () => {
      chartRef.current?.destroy();
      chartRef.current = null;
    };
  }, []);

  useEffect(() => {
    chartRef.current?.setData(line);
  }, [line]);

  useEffect(() => {
    chartRef.current?.set0dteVisible(show0dte);
  }, [show0dte]);

  // Second → sampled point, matching the chart's per-second de-duplication.
  const bySecond = useMemo(() => {
    const m = new Map<number, LinePoint>();
    for (const pt of line) m.set(Math.floor(pt.t / 1000), pt);
    return m;
  }, [line]);
  const hoverPoint = hoverSec === null ? null : bySecond.get(hoverSec) ?? null;
  const liveSpot = latestSpot(line);

  return (
    <section className="tape-pane">
      <h2 className="tape-pane__label">Net Delta-Notional (running $M)</h2>
      <CurrentValues
        pane={pane}
        show0dte={show0dte}
        symbol={symbol}
        spot={liveSpot}
        hoverPoint={hoverPoint}
      />
      <div ref={ref} className="delta-chart" data-testid="delta-chart" />
    </section>
  );
}

/** The most recent sample with a usable spot (spot can drop out mid-session). */
function latestSpot(line: LinePoint[]): number | null {
  for (let i = line.length - 1; i >= 0; i--) {
    const s = line[i]?.spot;
    if (typeof s === "number" && Number.isFinite(s) && s > 0) return s;
  }
  return null;
}
